import { HStack } from '@cardboard-ui/react';
import { CalendarAltIcon, FolderOpenIcon } from 'components/icons';
import SpaceIcon from 'components/SpaceIcon';
import React from 'react';
import { generatePath } from 'react-router-dom';
import { graphql, useFragment } from 'utils/graphClient';
import { CANONICAL_ITEM_PATH, EVENT_PATH, SPACE_PATH } from 'utils/routes';
import { TextLink } from '../TextLink';
import { ItemLocationInfo_item$key } from './__generated__/ItemLocationInfo_item.graphql';

const itemLocationFragment = graphql`
  fragment ItemLocationInfo_item on HasOwnerPermissionsInterface {
    ilSpace: ownershipSpace {
      id
      name
      color {
        light
        dark
      }
      icon {
        ... on NamedIcon {
          name
        }
      }
    }
    ilEvent: ownershipEvent {
      id
      name
    }
    ilFolders: ownershipFolders(first: 1) {
      nodes {
        id
        name
      }
    }
  }
`;

export const ItemLocationInfo = ({
  item,
  showSpace = true,
}: {
  item: ItemLocationInfo_item$key;
  showSpace?: boolean;
}) => {
  const locationInfo = useFragment(itemLocationFragment, item);
  const {
    ilEvent: event,
    ilFolders: folderNodes,
    ilSpace: space,
  } = locationInfo;
  const [folder] = folderNodes.nodes;

  if (event && space) {
    return (
      <HStack fontSize="xs" spacing={0.5} display="inline-flex">
        <TextLink to={generatePath(EVENT_PATH, { eventId: event.id })}>
          <CalendarAltIcon />
          {event.name}
        </TextLink>
        {showSpace && (
          <TextLink to={generatePath(SPACE_PATH, { spaceId: space.id })}>
            <SpaceIcon name={space.icon.name} color={space.color?.light} />
            {space.name}
          </TextLink>
        )}
      </HStack>
    );
  }

  if (folder) {
    return (
      <HStack fontSize="xs" spacing={0.5} display="inline-flex">
        <FolderOpenIcon />
        <TextLink to={generatePath(CANONICAL_ITEM_PATH, { itemId: folder.id })}>
          {folder.name}
        </TextLink>
        {showSpace && (
          <TextLink to={generatePath(SPACE_PATH, { spaceId: space.id })}>
            <SpaceIcon name={space.icon.name} color={space.color?.light} />
            {space.name}
          </TextLink>
        )}
      </HStack>
    );
  }

  if (space && showSpace) {
    return (
      <HStack fontSize="xs" spacing={0.5} display="inline-flex">
        <SpaceIcon name={space.icon.name} color={space.color?.light} />
        <TextLink to={generatePath(SPACE_PATH, { spaceId: space.id })}>
          {space.name}
        </TextLink>
      </HStack>
    );
  } else {
    return null;
  }
};

export const useHasLocationInfo = ({
  item,
  showSpace = true,
}: {
  item: ItemLocationInfo_item$key;
  showSpace?: boolean;
}) => {
  const locationInfo = useFragment(itemLocationFragment, item);
  const {
    ilEvent: event,
    ilFolders: folderNodes,
    ilSpace: space,
  } = locationInfo;
  const [folder] = folderNodes.nodes;

  if (event && space) {
    return true;
  }

  if (folder) {
    return true;
  }

  if (space && showSpace) {
    return true;
  }

  return false;
};
